import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import ActionDialog from "components/admin/ActionDialog";
import SelectField from "components/admin/SelectField";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useFetch, useRunner } from "components/admin/hooks";
import { useConfirm } from "components/admin/Feedback";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";

/** CollectionManager.TAB_*: 0..4 là trang ô, 5 là mốc tổng. */
const TAB_PET = 0;
const TAB_TOTAL = 5;
const STATS = ["atk", "def", "hp", "mp"];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function configForm(data) {
  const c = data.config;
  return {
    ratio: c.ratio,
    star_ratio: c.star_ratio,
    cap_atk: c.cap_atk,
    cap_def: c.cap_def,
    cap_hp: c.cap_hp,
    cap_mp: c.cap_mp,
  };
}

function milestoneForm(data) {
  return data.milestones.map((m) => ({
    id: m.id,
    tab: m.tab,
    need: m.need,
    star: m.star,
    gift: m.gift,
    sort: m.sort,
  }));
}

const statText = (e) =>
  STATS.filter((k) => e[k] > 0)
    .map((k) => `${k.toUpperCase()} ${formatNumber(e[k])}`)
    .join(" · ") || "—";

// ------------------------------------------------------------------ ô

function EntryDialog({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: entry.name,
    icon: entry.icon,
    source_text: entry.source_text,
    hidden: entry.hidden,
    sort: entry.sort,
  });
  const [run, busy] = useRunner();
  const save = async () => {
    const { ok, result } = await run(
      () =>
        api.put(`/api/admin/collection/entry/${entry.id}`, {
          ...form,
          sort: Number(form.sort) || 0,
        }),
      "Đã lưu ô"
    );
    if (ok) onSaved(result);
  };
  return (
    <ActionDialog
      open
      title={`Ô #${entry.id}`}
      description={`Mẫu: ${entry.ids.join(", ")}${
        entry.is_set ? " (mỗi mẫu là một mảnh của bộ)" : ""
      }. Không sửa được mẫu để không lệch dữ liệu đã kích hoạt.`}
      color="info"
      submitText="Lưu"
      disabled={busy || !form.name.trim()}
      onClose={onClose}
      onSubmit={save}
    >
      <MDBox display="flex" flexDirection="column" gap={2}>
        <MDInput
          label="Tên"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <MDInput
          label="Ảnh (đường dẫn trên CDN)"
          value={form.icon}
          onChange={(e) => setForm({ ...form, icon: e.target.value })}
        />
        <MDInput
          label='Dòng "Nguồn: ..." hiện ở ô chưa có'
          value={form.source_text}
          onChange={(e) => setForm({ ...form, source_text: e.target.value })}
        />
        <MDInput
          type="number"
          label="Thứ tự trong trang"
          value={form.sort}
          onChange={(e) => setForm({ ...form, sort: e.target.value })}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.hidden}
              onChange={(e) => setForm({ ...form, hidden: e.target.checked })}
            />
          }
          label="Ẩn: không hiện, không kích hoạt được, không cộng chỉ số, không tính mốc"
        />
      </MDBox>
    </ActionDialog>
  );
}

EntryDialog.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.number,
    ids: PropTypes.arrayOf(PropTypes.number),
    is_set: PropTypes.bool,
    name: PropTypes.string,
    icon: PropTypes.string,
    source_text: PropTypes.string,
    hidden: PropTypes.bool,
    sort: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function EntryList({ data, canEdit, onSaved }) {
  const [tab, setTab] = useState(TAB_PET);
  const [query, setQuery] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [editing, setEditing] = useState(null);
  const [run, busy] = useRunner();
  const confirm = useConfirm();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.entries.filter(
      (e) =>
        e.tab === tab &&
        (showHidden || !e.hidden) &&
        (!q || e.name.toLowerCase().includes(q) || e.ids.some((id) => String(id) === q))
    );
  }, [data, tab, query, showHidden]);

  const reseed = async () => {
    const yes = await confirm({
      title: "Sinh ô cho món mới",
      message:
        "Thêm ô cho các pet / vật phẩm / hình xăm mới có trong dữ liệu game mà bộ sưu tập chưa có. Ô đã có giữ nguyên.",
      confirmText: "Sinh ô mới",
    });
    if (!yes) return;
    const { ok, result } = await run(
      () => api.post("/api/admin/collection/reseed"),
      (r) => `Đã thêm ${r.added} ô cho mẫu mới`
    );
    if (ok) onSaved(result.data);
  };

  return (
    <PageCard
      title="Danh sách ô"
      subtitle="Chỉ số là mức ô cộng khi kích hoạt đủ, ở 0 sao, theo tỉ lệ đang lưu"
      color="info"
      actions={
        canEdit && (
          <Tooltip title="Thêm ô cho pet / item / hình xăm mới thêm vào DB. Ô đã có giữ nguyên">
            <MDButton size="small" color="white" disabled={busy} onClick={reseed}>
              <Icon>playlist_add</Icon>&nbsp;Sinh ô cho món mới
            </MDButton>
          </Tooltip>
        )
      }
    >
      <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
        {data.tabs.slice(0, TAB_TOTAL).map((name, i) => {
          const all = data.entries.filter((e) => e.tab === i);
          return (
            <Chip
              key={name}
              label={`${name} (${all.filter((e) => !e.hidden).length} hiện / ${all.length})`}
              color={tab === i ? "info" : "default"}
              onClick={() => setTab(i)}
            />
          );
        })}
        <MDInput
          size="small"
          label="Tìm tên hoặc mã mẫu"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <FormControlLabel
          control={
            <Switch checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
          }
          label="Hiện cả ô ẩn"
        />
      </MDBox>
      {rows.map((e) => (
        <MDBox
          key={e.id}
          display="flex"
          alignItems="center"
          gap={1}
          py={0.5}
          sx={{ borderBottom: "1px solid #eee", opacity: e.hidden ? 0.5 : 1 }}
        >
          <MDBox flex={1} minWidth={0}>
            <MDTypography variant="button" fontWeight="medium">
              <Tooltip title={`mã ô ${e.id}`}>
                <span>{e.name}</span>
              </Tooltip>
              {e.hidden && <Chip size="small" label="ẩn" sx={{ ml: 1, height: 18 }} />}
              {e.is_set && (
                <Chip size="small" label={`${e.ids.length} mảnh`} sx={{ ml: 1, height: 18 }} />
              )}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              {e.source_text ? `Nguồn: ${e.source_text}` : "Chưa ghi nguồn"}
              <span style={{ opacity: 0.6 }}> · mã mẫu {e.ids.join(", ")}</span>
            </MDTypography>
          </MDBox>
          <MDTypography variant="caption" sx={{ width: 260 }}>
            {statText(e)}
          </MDTypography>
          <Chip size="small" label={`${formatNumber(e.unlocks)} người có`} />
          {canEdit && (
            <Tooltip title="Sửa tên, ảnh, nguồn, ẩn / hiện">
              <IconButton size="small" aria-label="Sửa ô" onClick={() => setEditing(e)}>
                <Icon fontSize="small">edit</Icon>
              </IconButton>
            </Tooltip>
          )}
        </MDBox>
      ))}
      {rows.length === 0 && <Alert severity="info">Không có ô nào khớp.</Alert>}
      {editing && (
        <EntryDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            setEditing(null);
            onSaved(d);
          }}
        />
      )}
    </PageCard>
  );
}

EntryList.propTypes = {
  data: PropTypes.shape({
    entries: PropTypes.arrayOf(PropTypes.object),
    tabs: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ------------------------------------------------------------------ mốc

function MilestoneDialog({ tabs, row, onClose, onApply }) {
  const [form, setForm] = useState(row);
  const bad = !form.gift || form.gift.length === 0 || (form.star > 0 && form.tab !== TAB_PET);
  return (
    <ActionDialog
      open
      title={row.id ? `Sửa mốc #${row.id}` : "Thêm mốc"}
      description="Bấm Áp dụng rồi bấm Lưu mốc để lưu lên máy chủ. Vật phẩm nên khoá giao dịch."
      color="info"
      submitText="Áp dụng"
      disabled={bad}
      onClose={onClose}
      onSubmit={() =>
        onApply({ ...form, need: Number(form.need) || 0, star: Number(form.star) || 0 })
      }
      maxWidth="md"
    >
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <SelectField
            fullWidth
            label="Trang"
            value={form.tab}
            onChange={(e) => setForm({ ...form, tab: Number(e.target.value) })}
          >
            {tabs.map((t, i) => (
              <MenuItem key={t} value={i}>
                {t}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Số ô cần"
            helperText="0 = đủ mọi ô đang hiện"
            value={form.need}
            onChange={(e) => setForm({ ...form, need: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <MDInput
            fullWidth
            type="number"
            label="Sao (chỉ trang Pet)"
            helperText="0 = mốc số ô; 1..5 = số mẫu pet đạt ít nhất số sao này"
            value={form.star}
            onChange={(e) => setForm({ ...form, star: e.target.value })}
          />
        </Grid>
      </Grid>
      <GiftEditor
        value={form.gift}
        onChange={(gift) => setForm({ ...form, gift })}
        label="Quà mốc"
      />
    </ActionDialog>
  );
}

MilestoneDialog.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  row: PropTypes.shape({
    id: PropTypes.number,
    tab: PropTypes.number,
    need: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    star: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gift: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

function Milestones({ data, canEdit, onSaved }) {
  const [form, setForm] = useState(() => milestoneForm(data));
  const [editing, setEditing] = useState(null);
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  useEffect(() => setForm(milestoneForm(data)), [data]);
  const dirty = !same(milestoneForm(data), form);

  const save = async () => {
    const tabName = (t) => data.tabs[t] || "";
    const added = form.filter((m) => !m.id);
    const removed = data.milestones.filter((s) => !form.some((m) => m.id === s.id));
    const edited = form.filter((m) => {
      const s = data.milestones.find((x) => x.id === m.id && m.id > 0);
      return s && !same(milestoneForm({ milestones: [s] })[0], m);
    });
    const removedClaimed = removed.filter((s) => s.claims > 0);
    const lines = [
      added.length
        ? `Thêm ${added.length} mốc: ${added
            .map((m) => `${tabName(m.tab)} ${label(m)}`)
            .join(", ")}.`
        : null,
      edited.length
        ? `Sửa ${edited.length} mốc: ${edited
            .map((m) => `${tabName(m.tab)} ${label(m)}`)
            .join(", ")}.`
        : null,
      removed.length
        ? `Bỏ ${removed.length} mốc: ${removed
            .map((m) => `${tabName(m.tab)} ${label(m)}`)
            .join(", ")}.`
        : null,
      removedClaimed.length
        ? `Chú ý: mốc bị bỏ đã có ${removedClaimed.reduce(
            (n, m) => n + m.claims,
            0
          )} lượt nhận; người đã nhận vẫn giữ quà.`
        : null,
      "Áp dụng ngay cho mọi người chơi.",
    ].filter(Boolean);
    const yes = await confirm({
      title: "Lưu mốc quà bộ sưu tập",
      message: lines.join("\n"),
      color: removedClaimed.length ? "warning" : "info",
      confirmText: "Lưu mốc quà",
    });
    if (!yes) return;
    const { ok, result } = await run(
      () => api.put("/api/admin/collection/milestones", form),
      "Đã lưu mốc quà"
    );
    if (ok) onSaved(result);
  };

  const label = (m) =>
    m.star > 0
      ? `${m.need} mẫu pet ${m.star}★`
      : m.need > 0
      ? `${m.need} ô`
      : m.tab === TAB_TOTAL
      ? "đủ mọi ô"
      : "đủ trang";

  return (
    <PageCard
      title="Mốc quà"
      subtitle="Mỗi tài khoản nhận mỗi mốc một lần. Ô bộ trang bị chỉ tính khi đủ mảnh"
      color="success"
      actions={
        canEdit && (
          <MDBox display="flex" gap={1}>
            <MDButton size="small" color="white" onClick={() => setEditing({ index: -1 })}>
              <Icon>add</Icon>&nbsp;Thêm
            </MDButton>
            {dirty && (
              <MDButton
                size="small"
                color="white"
                disabled={busy}
                onClick={() => setForm(milestoneForm(data))}
              >
                <Icon>undo</Icon>&nbsp;Bỏ sửa
              </MDButton>
            )}
            <MDButton size="small" color="white" disabled={!dirty || busy} onClick={save}>
              <Icon>save</Icon>&nbsp;Lưu mốc
            </MDButton>
          </MDBox>
        )
      }
    >
      <Grid container spacing={2}>
        {data.tabs.map((tabName, t) => (
          <Grid item xs={12} md={6} lg={4} key={tabName}>
            <MDTypography variant="h6" mb={1}>
              {tabName}
            </MDTypography>
            {form.map((m, i) => {
              if (m.tab !== t) return null;
              const saved = data.milestones.find((x) => x.id === m.id && m.id > 0);
              const unchanged = saved && same(saved.gift, m.gift);
              return (
                <MDBox key={m.id || `new-${i}`} display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip size="small" label={label(m)} color={m.star > 0 ? "warning" : "default"} />
                  <MDBox flex={1}>
                    <GiftPreview gift={m.gift} lines={unchanged ? saved.giftLines : null} />
                  </MDBox>
                  {saved && (
                    <Tooltip title="Số tài khoản đã nhận">
                      <Chip size="small" label={formatNumber(saved.claims)} />
                    </Tooltip>
                  )}
                  {canEdit && (
                    <>
                      <IconButton size="small" onClick={() => setEditing({ index: i })}>
                        <Icon fontSize="small">edit</Icon>
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setForm(form.filter((_, j) => j !== i))}
                      >
                        <Icon fontSize="small">delete</Icon>
                      </IconButton>
                    </>
                  )}
                </MDBox>
              );
            })}
          </Grid>
        ))}
      </Grid>
      {editing && (
        <MilestoneDialog
          tabs={data.tabs}
          row={
            editing.index < 0
              ? { id: 0, tab: TAB_PET, need: 10, star: 0, gift: [], sort: 0 }
              : form[editing.index]
          }
          onClose={() => setEditing(null)}
          onApply={(row) => {
            setForm(
              editing.index < 0
                ? [...form, row]
                : form.map((m, i) => (i === editing.index ? row : m))
            );
            setEditing(null);
          }}
        />
      )}
    </PageCard>
  );
}

Milestones.propTypes = {
  data: PropTypes.shape({
    milestones: PropTypes.arrayOf(PropTypes.object),
    tabs: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ------------------------------------------------------------------ tra người chơi

function PlayerLookup({ data, canEdit }) {
  const canSearch = useAuth().can(PERMISSIONS.PLAYER_VIEW);
  const confirm = useConfirm();
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState(null);
  const [who, setWho] = useState(null);
  const [result, setResult] = useState(null);
  const [unlocking, setUnlocking] = useState(null);
  const [run, busy] = useRunner();
  const entryOptions = useMemo(
    () =>
      data.entries
        .filter((e) => !e.hidden)
        .map((e) => ({ ...e, label: `${data.tabs[e.tab]} · ${e.name}` })),
    [data]
  );

  const load = async (person) => {
    setWho(person);
    setMatches(null);
    const { ok, result: r } = await run(() =>
      api.get(`/api/admin/collection/player/${person.userId}`)
    );
    if (ok) setResult(r);
  };

  // Có quyền xem người chơi thì tìm theo tên; không thì chỉ nhập được mã tài khoản (số).
  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setResult(null);
    if (!canSearch) {
      if (/^\d+$/.test(q)) load({ userId: Number(q), name: null });
      return;
    }
    const { ok, result: r } = await run(() => api.get("/api/admin/players", { q, size: 10 }));
    if (!ok) return;
    const rows = r?.items || r || [];
    const lower = q.toLowerCase();
    const exact = rows.filter(
      (p) =>
        (p.name || "").toLowerCase() === lower ||
        (p.username || "").toLowerCase() === lower ||
        String(p.userId) === q
    );
    if (exact.length === 1) load(exact[0]);
    else if (rows.length === 1) load(rows[0]);
    else setMatches(rows);
  };

  const target = unlocking?.entry || null;
  const whoName = who?.name || `tài khoản ${result?.userId}`;

  const unlock = async () => {
    const yes = await confirm({
      title: "Kích hoạt hộ",
      message: `Kích hoạt ô "${target.name}" (${data.tabs[target.tab]})${
        target.tab === TAB_PET ? ` ở ${Number(unlocking.star) || 0} sao` : ""
      } cho ${whoName}.\n\nNgười chơi được cộng chỉ số ngay, không thu hồi được. Có ghi lịch sử kèm lý do.`,
      color: "warning",
      confirmText: "Kích hoạt hộ",
    });
    if (!yes) return;
    const { ok, result: r } = await run(
      () =>
        api.post(`/api/admin/collection/player/${result.userId}/unlock`, {
          entryId: target.id,
          star: Number(unlocking.star) || 0,
          reason: unlocking.reason,
        }),
      `Đã kích hoạt "${target.name}" cho ${whoName}`
    );
    if (ok) {
      setResult(r);
      setUnlocking(null);
    }
  };

  return (
    <PageCard
      title="Tra người chơi"
      subtitle="Khách hỏi về bộ sưu tập: xem ô đã kích hoạt, mốc đã nhận, chỉ số đang được cộng"
      color="info"
    >
      <MDBox
        component="form"
        display="flex"
        gap={1}
        alignItems="center"
        flexWrap="wrap"
        mb={2}
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <MDInput
          size="small"
          label={canSearch ? "Tên nhân vật hoặc tên tài khoản" : "Mã tài khoản (số)"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <MDButton type="submit" size="small" color="info" disabled={busy || !query.trim()}>
          <Icon>search</Icon>&nbsp;Xem
        </MDButton>
        {result && canEdit && (
          <MDButton
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => setUnlocking({ entry: null, star: 0, reason: "" })}
          >
            <Icon>lock_open</Icon>&nbsp;Kích hoạt hộ
          </MDButton>
        )}
      </MDBox>
      {matches && matches.length === 0 && (
        <Alert severity="info">Không tìm thấy ai khớp &quot;{query.trim()}&quot;.</Alert>
      )}
      {matches && matches.length > 1 && (
        <MDBox mb={2}>
          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
            Có {matches.length} người khớp, chọn đúng người:
          </MDTypography>
          <MDBox display="flex" gap={0.5} flexWrap="wrap">
            {matches.map((p) => (
              <Chip
                key={p.userId}
                label={`${p.name || "(chưa tạo nhân vật)"} · ${p.username || p.userId}`}
                onClick={() => load(p)}
              />
            ))}
          </MDBox>
        </MDBox>
      )}
      {result && (
        <>
          {who?.name && (
            <MDTypography variant="button" display="block" mb={1}>
              <Link to={`/players/${who.userId}`}>{who.name}</Link>
              {who.username ? ` · tài khoản ${who.username}` : ""}
            </MDTypography>
          )}
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
            <Chip label={`${result.unlocks.length} ô đã kích hoạt`} color="success" />
            <Chip label={`${result.claimed.length} mốc quà đã nhận`} />
            <Chip
              label={
                result.bonus
                  ? `Đang được cộng: ${STATS.map(
                      (k) => `${k.toUpperCase()} ${formatNumber(result.bonus[k])}`
                    ).join(" · ")}`
                  : "Chưa được cộng chỉ số"
              }
            />
          </MDBox>
          {result.unlocks.length === 0 && (
            <MDTypography variant="caption" color="text">
              Người này chưa kích hoạt ô nào.
            </MDTypography>
          )}
          <MDBox display="flex" gap={0.5} flexWrap="wrap">
            {result.unlocks.map((u) => (
              <Chip
                key={u.entry_id}
                size="small"
                label={`${data.tabs[u.tab] || "?"} · ${u.name}${
                  u.tab === TAB_PET ? ` ${u.max_star}★` : ""
                }${
                  u.pieces > 0
                    ? ` (${u.piece_mask.toString(2).split("1").length - 1}/${u.pieces} mảnh)`
                    : ""
                }`}
              />
            ))}
          </MDBox>
        </>
      )}
      {unlocking && (
        <ActionDialog
          open
          title={`Kích hoạt hộ cho ${whoName}`}
          description="Chỉ dùng khi người chơi gửi bằng chứng (ví dụ đã có món nhưng bị lỗi không kích hoạt được). Ô bộ trang bị được mở đủ mảnh."
          color="warning"
          submitText="Kích hoạt"
          disabled={busy || !target || unlocking.reason.trim().length < 5}
          onClose={() => setUnlocking(null)}
          onSubmit={unlock}
        >
          <MDBox display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              options={entryOptions}
              value={target}
              onChange={(e, v) => setUnlocking({ ...unlocking, entry: v })}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <MDInput {...params} label="Chọn ô (gõ tên để tìm)" />
              )}
            />
            {target && target.tab === TAB_PET && (
              <MDInput
                type="number"
                label="Số sao của pet (0 – 5)"
                value={unlocking.star}
                inputProps={{ min: 0, max: 5 }}
                onChange={(e) => setUnlocking({ ...unlocking, star: e.target.value })}
              />
            )}
            <MDInput
              label="Lý do (mã khiếu nại, bằng chứng)"
              value={unlocking.reason}
              onChange={(e) => setUnlocking({ ...unlocking, reason: e.target.value })}
              helperText={
                unlocking.reason.trim().length < 5
                  ? "Cần ít nhất 5 ký tự để sau này tra lại"
                  : "Ghi vào lịch sử"
              }
            />
            {!target && (
              <MDTypography variant="caption" color="text">
                Chọn ô cần kích hoạt.
              </MDTypography>
            )}
          </MDBox>
        </ActionDialog>
      )}
    </PageCard>
  );
}

PlayerLookup.propTypes = {
  data: PropTypes.shape({
    entries: PropTypes.arrayOf(PropTypes.object),
    tabs: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

// ------------------------------------------------------------------ trang

// Tab ghi lên URL (?tab=). Tỉ lệ & trần ảnh hưởng cân bằng sức mạnh nên để cuối, ghi rõ "kỹ thuật".
const TABS = [
  { key: "entries", label: "Danh sách ô" },
  { key: "milestones", label: "Mốc quà" },
  { key: "lookup", label: "Tra người chơi" },
  { key: "config", label: "Tỉ lệ & trần (kỹ thuật)" },
];
const TAB_SX = { flex: { xs: "0 0 auto", md: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

const CONFIG_LABELS = {
  ratio: "% mỗi ô",
  star_ratio: "% thêm mỗi sao pet",
  cap_atk: "Trần ATK",
  cap_def: "Trần DEF",
  cap_hp: "Trần HP",
  cap_mp: "Trần MP",
};

function Collection() {
  const canEdit = useAuth().can(PERMISSIONS.COLLECTION_EDIT);
  const page = useFetch(() => api.get("/api/admin/collection"), []);
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [form, setForm] = useState(null);
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "entries";
  const pickTab = (v) => setParams(v === "entries" ? {} : { tab: v }, { replace: true });

  useEffect(() => {
    if (page.data) setForm(configForm(page.data));
  }, [page.data]);

  const data = page.data;
  const dirty = data && form && !same(configForm(data), form);
  const set = (patch) => setForm({ ...form, ...patch });

  // Rời trang khi tỉ lệ / trần còn thay đổi chưa lưu thì trình duyệt hỏi lại.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const saveConfig = async () => {
    const before = configForm(data);
    const changes = Object.keys(CONFIG_LABELS)
      .filter((k) => Number(before[k]) !== Number(form[k]))
      .map(
        (k) =>
          `• ${CONFIG_LABELS[k]}: ${formatNumber(before[k]) || 0} → ${
            formatNumber(Number(form[k]) || 0) || 0
          }`
      );
    const yes = await confirm({
      title: "Lưu tỉ lệ & trần chỉ số",
      message: [
        "Áp dụng cho chỉ số pet của MỌI người chơi (cả PvP) ở lần tính chỉ số kế tiếp:",
        "",
        ...changes,
        "",
        "Nâng tỉ lệ hoặc trần có thể làm pet quá mạnh — nên hỏi kỹ thuật / chạy BalanceCheck trước.",
      ].join("\n"),
      color: "warning",
      confirmText: "Lưu và áp dụng",
    });
    if (!yes) return;
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v) || 0]));
    const { ok, result } = await run(
      () => api.put("/api/admin/collection/config", body),
      "Đã lưu. Người chơi thấy chỉ số mới ở lần tính chỉ số kế tiếp"
    );
    if (ok) page.setData(result);
  };

  const field = (key, helperText) => (
    <Grid item xs={6} md={2} key={key}>
      <MDInput
        fullWidth
        type="number"
        label={CONFIG_LABELS[key]}
        value={form[key]}
        disabled={!canEdit}
        helperText={helperText}
        inputProps={{ min: 0 }}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="collection" />
        {page.error && (
          <Alert severity="error">Không tải được dữ liệu bộ sưu tập: {page.error}</Alert>
        )}
        {data && form && (
          <>
            <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip label={`${formatNumber(data.stats.players)} người đã kích hoạt`} />
              <Chip label={`${formatNumber(data.stats.unlocks)} lượt kích hoạt`} />
              <Chip label={`${formatNumber(data.stats.claims)} lượt nhận mốc`} />
              <Chip label={`${formatNumber(data.stats.byAdmin)} ô admin kích hoạt hộ`} />
              {dirty && <Chip color="warning" label="Tỉ lệ & trần có thay đổi chưa lưu" />}
            </MDBox>
            <Tabs
              value={tab}
              onChange={(e, v) => pickTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 1, maxWidth: 760 }}
            >
              {TABS.map((t) => (
                <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
              ))}
            </Tabs>

            {tab === "config" && (
              <MDBox mb={3}>
                <PageCard
                  title="Tỉ lệ & trần chỉ số"
                  subtitle="Mỗi ô cộng thêm (% mỗi ô + % mỗi sao × số sao) chỉ số gốc của món cho mọi pet; tổng cả bộ không vượt trần"
                  color="warning"
                  actions={
                    canEdit && (
                      <MDBox display="flex" gap={1}>
                        {dirty && (
                          <MDButton
                            size="small"
                            color="white"
                            disabled={busy}
                            onClick={() => setForm(configForm(data))}
                          >
                            <Icon>undo</Icon>&nbsp;Bỏ sửa
                          </MDButton>
                        )}
                        <MDButton
                          size="small"
                          color="white"
                          disabled={!dirty || busy}
                          onClick={saveConfig}
                        >
                          <Icon>save</Icon>&nbsp;Lưu
                        </MDButton>
                      </MDBox>
                    )
                  }
                >
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Phần này ảnh hưởng sức mạnh pet của mọi người chơi. Chỉ sửa khi kỹ thuật / người
                    cân bằng game yêu cầu. (Trần HP 150.000 / DEF 30.000 đang giữ BalanceCheck A4
                    xanh; nâng trần hoặc tỉ lệ thì chạy lại BalanceCheck trước.)
                  </Alert>
                  <Grid container spacing={2}>
                    {field("ratio", "5 = 5%")}
                    {field("star_ratio", "chỉ ô pet")}
                    {field("cap_atk", "0 = không trần")}
                    {field("cap_def", "0 = không trần")}
                    {field("cap_hp", "0 = không trần")}
                    {field("cap_mp", "0 = không trần")}
                  </Grid>
                </PageCard>
              </MDBox>
            )}
            {tab === "entries" && (
              <MDBox mb={3}>
                <EntryList data={data} canEdit={canEdit} onSaved={page.setData} />
              </MDBox>
            )}
            {tab === "milestones" && (
              <MDBox mb={3}>
                <Milestones data={data} canEdit={canEdit} onSaved={page.setData} />
              </MDBox>
            )}
            {tab === "lookup" && <PlayerLookup data={data} canEdit={canEdit} />}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Collection;
